import type { FC } from 'react';
import React from 'react';
import { URLS } from '@shared/utils/urls';
import type { BaseBoxProps } from '@withjoy/joykit';
import { getDeviceResolution } from '@shared/utils/deviceResolution';
import { GraphicAccentContainer, GraphicAccentImg } from './GraphicAccent.styles';
import { InlineEditor } from '@apps/guest/shared/components/InlineEditor';
import type { EventPageType } from '@graphql/generated-base';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTranslation } from '@shared/core';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';

type GraphicAccentProps = Readonly<
  BaseBoxProps & {
    id?: string;
    accentId: string;
    /** Override the image scale (1, 2, or 3). Defaults to the device pixel ratio. */
    scale?: 1 | 2 | 3;
    isInlineEditable?: boolean;
    pageName?: Maybe<EventPageType>;
    pageSlug?: string;
  }
>;

type GraphicAccentWrapperProps = Readonly<{
  children: React.ReactNode;
  isInlineEditable: boolean;
  pageName: Maybe<EventPageType>;
  pageSlug: string;
  accentId: string;
}>;

const DEVICE_RESOLUTION = getDeviceResolution() as 1 | 2 | 3;

const GraphicAccentWrapper: FC<GraphicAccentWrapperProps> = props => {
  const { children, isInlineEditable, pageName, pageSlug, accentId } = props;

  const { t } = useTranslation('joykit');
  const inlineEditorTransFn = t('inlineEditor');

  const onEditClick = useEventCallback(() => {
    // send the message to the parent window to open the text editor
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: {
        action: 'editGraphicAccent',
        inlineEditData: { accentId },
      },
    });
  });

  return isInlineEditable ? (
    <InlineEditor
      containerCSS={{ minWidth: pxToRem(200) }}
      actionData={{ edit: onEditClick }}
      wrapperType="actionOutside"
      componentName="graphicAccent"
      elementLabel={inlineEditorTransFn.graphicAccent()}
      pageName={pageName}
      pageSlug={pageSlug}
    >
      {children}
    </InlineEditor>
  ) : (
    <>{children}</>
  );
};

export const GraphicAccent: React.FC<GraphicAccentProps> = props => {
  const { accentId, scale, isInlineEditable = false, pageName, pageSlug = '', ...restProps } = props;
  const effectiveScale = scale ?? DEVICE_RESOLUTION;
  return (
    <GraphicAccentWrapper
      isInlineEditable={isInlineEditable}
      pageName={pageName}
      pageSlug={pageSlug}
      accentId={accentId}
    >
      <GraphicAccentContainer {...restProps}>
        <GraphicAccentImg
          src={URLS.graphicAccent(accentId, effectiveScale)}
          pixelDensity={effectiveScale}
          alt=""
        />
      </GraphicAccentContainer>
    </GraphicAccentWrapper>
  );
};
